import type { DeskMetric, DeskMetricPoint, DeskPulseState } from '@deskworks/shared-types';
import { Link } from '@tanstack/react-router';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { type PointerEvent, useId, useMemo, useState } from 'react';
import { Badge, dotTone } from '@/components/ui/badge';
import { formatAbsolute } from '@/lib/format';
import { intlLocale, t } from '@/lib/i18n';
import { cn } from '@/lib/utils';

type Tone = 'neutral' | 'muted' | 'blue' | 'accent' | 'amber';

export const PULSE: Record<DeskPulseState, { label: string; tone: Tone; live?: boolean; hint: string }> = {
  needs_you: { label: t('Needs you'), tone: 'amber', live: true, hint: t('An approval, an action or a question waits for a person.') },
  working: { label: t('Working'), tone: 'blue', live: true, hint: t('Agents of this desk are working right now.') },
  checking: { label: t('Checking'), tone: 'accent', live: true, hint: t('The Lead or a reviewer is checking the desk’s work.') },
  watching: { label: t('Watching'), tone: 'neutral', hint: t('Nothing runs; the desk waits for its next trigger.') },
  paused: { label: t('Paused'), tone: 'muted', hint: t('The desk is paused: no triggers, no new runs.') },
};

export function PulseBadge({ state, className }: { state: DeskPulseState; className?: string }) {
  const meta = PULSE[state];
  return (
    <Badge tone={meta.tone} title={meta.hint} className={cn('justify-center transition-colors duration-500', className)}>
      <span className={cn('size-1.5 rounded-full', dotTone[meta.tone], meta.live && 'animate-soft-pulse')} />
      {meta.label}
    </Badge>
  );
}

const PREFIX_UNITS = new Set(['€', '$', '£']);

/** 1234.5 with unit '€' → €1,234.5; with '%' → 1,234.5%; with 'ms' → 1,234.5 ms. */
export function formatMetric(value: number, unit: string) {
  const n = new Intl.NumberFormat(intlLocale, {
    maximumFractionDigits: Math.abs(value) >= 100 ? 0 : 2,
    notation: Math.abs(value) >= 100_000 ? 'compact' : 'standard',
  }).format(value);
  if (!unit) return n;
  if (PREFIX_UNITS.has(unit)) return `${unit}${n}`;
  if (unit === '%' || unit === '×') return `${n}${unit}`;
  return `${n} ${unit}`;
}

/** Change over the shown points, coloured by whether that direction is good. */
export function MetricTrend({ metric, className }: { metric: DeskMetric; className?: string }) {
  const first = metric.points[0];
  const last = metric.latest;
  if (!first || !last || metric.points.length < 2 || first.value === last.value) return null;
  const up = last.value > first.value;
  const good = metric.good === 'none' ? null : (metric.good === 'up') === up;
  const pct = first.value !== 0 ? ((last.value - first.value) / Math.abs(first.value)) * 100 : null;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      title={t('Since {date}: {from} → {to}', { date: formatAbsolute(first.at), from: formatMetric(first.value, metric.unit), to: formatMetric(last.value, metric.unit) })}
      className={cn(
        'inline-flex items-center gap-0.5 text-[11px] font-medium tabular-nums',
        good === null ? 'text-muted-foreground' : good ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400',
        className,
      )}
    >
      <Icon className="size-3" />
      {pct !== null ? `${Math.abs(pct) >= 10 ? Math.abs(pct).toFixed(0) : Math.abs(pct).toFixed(1)}%` : formatMetric(Math.abs(last.value - first.value), metric.unit)}
    </span>
  );
}

const ms = (p: { at: string }) => new Date(p.at).getTime();

/** The value a series had at time `at`: its latest point at or before it (its first one before it began). */
function valueAt(points: DeskMetricPoint[], at: number) {
  return (points.findLast((p) => ms(p) <= at) ?? points[0])?.value;
}

/** The overlay within [t0, t1] as steps: the value it had at t0, then each change, held to the right edge. */
function stepPath(points: DeskMetricPoint[], t0: number, t1: number, width: number, height: number) {
  const before = points.findLast((p) => ms(p) <= t0);
  const steps = [...(before ? [{ t: t0, value: before.value }] : []), ...points.filter((p) => ms(p) > t0 && ms(p) <= t1).map((p) => ({ t: ms(p), value: p.value }))];
  if (!steps.length) return null;
  const pad = 3;
  const v0 = Math.min(...steps.map((s) => s.value));
  const v1 = Math.max(...steps.map((s) => s.value));
  const x = (time: number) => (t1 === t0 ? width : ((time - t0) / (t1 - t0)) * width);
  const y = (v: number) => (v1 === v0 ? height / 2 : pad + (1 - (v - v0) / (v1 - v0)) * (height - pad * 2));
  return `M${x(steps[0]!.t).toFixed(1)},${y(steps[0]!.value).toFixed(1)}${steps
    .slice(1)
    .map((s) => ` H${x(s.t).toFixed(1)} V${y(s.value).toFixed(1)}`)
    .join('')} H${width}`;
}

/**
 * The chart's time axis: from the first point to the last one of either series. A series with fewer than two values
 * of its own (just set up) spans the overlay's time, so both still show.
 */
function chartPaths(points: DeskMetricPoint[], overlay: DeskMetricPoint[], width: number, height: number) {
  const pad = 3;
  const times = points.map(ms);
  const values = points.map((p) => p.value);
  const t0 = Math.min(...times, ...(points.length >= 2 ? [] : overlay.map(ms)));
  const t1 = Math.max(...times, ...overlay.map(ms));
  if (!(t1 > t0)) return null;
  const v0 = Math.min(...values);
  const v1 = Math.max(...values);
  const x = (time: number) => (t1 === t0 ? width : ((time - t0) / (t1 - t0)) * width);
  const y = (v: number) => (v1 === v0 ? height / 2 : pad + (1 - (v - v0) / (v1 - v0)) * (height - pad * 2));
  const coords = points.map((p, i) => [x(times[i]!), y(p.value)] as const);
  const line = `M${coords.map(([cx, cy]) => `${cx.toFixed(1)},${cy.toFixed(1)}`).join(' L')}`;
  return {
    line: coords.length >= 2 ? line : null,
    /** A single value is held to the right edge, as the overlay's steps are. */
    held: coords.length === 1 ? `${line} H${width}` : null,
    area: `${line} L${coords.at(-1)![0].toFixed(1)},${height} L${coords[0]![0].toFixed(1)},${height} Z`,
    coords,
    overlay: overlay.length ? stepPath(overlay, t0, t1, width, height) : null,
  };
}

/**
 * The real series as a small area chart; a dashed line while there are fewer than two values. With `overlay`, a second
 * metric lies over it as a muted step line on its own scale (the same time axis). With `metric`, hovering shows the
 * values at the nearest point.
 */
export function MetricSparkline({
  points,
  metric,
  overlay,
  className,
}: {
  points: DeskMetricPoint[];
  metric?: DeskMetric;
  overlay?: DeskMetric | null;
  className?: string;
}) {
  const gradientId = `pulse-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const [hover, setHover] = useState<number | null>(null);
  const width = 100;
  const height = 36;
  const overlayPoints = overlay?.points ?? NO_POINTS;
  const paths = useMemo(() => (points.length ? chartPaths(points, overlayPoints, width, height) : null), [points, overlayPoints]);
  const last = paths?.coords.at(-1);
  const hovered = hover !== null && paths ? { point: points[hover]!, at: paths.coords[hover]! } : null;
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!paths || !metric) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * width;
    let nearest = 0;
    paths.coords.forEach(([cx], i) => {
      if (Math.abs(cx - x) < Math.abs(paths.coords[nearest]![0] - x)) nearest = i;
    });
    setHover(nearest);
  };
  return (
    <div aria-hidden className={cn('relative h-9 w-24 shrink-0', className)} onPointerMove={onPointerMove} onPointerLeave={() => setHover(null)}>
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="absolute inset-0 h-full w-full overflow-visible">
        {paths ? (
          <>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="currentColor" stopOpacity="0.28" />
                <stop offset="1" stopColor="currentColor" stopOpacity="0" />
              </linearGradient>
            </defs>
            {paths.held && (
              <path d={paths.held} fill="none" stroke="currentColor" strokeOpacity="0.5" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
            )}
            {paths.line && (
              <>
                <path d={paths.area} fill={`url(#${gradientId})`} />
                <path d={paths.line} fill="none" stroke="currentColor" strokeWidth="1.5" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
              </>
            )}
            {paths.overlay && (
              <path d={paths.overlay} className="text-foreground/45" fill="none" stroke="currentColor" strokeWidth="1.25" strokeDasharray="3 2" vectorEffect="non-scaling-stroke" />
            )}
            {hovered && <path d={`M${hovered.at[0]},0 V${height}`} stroke="currentColor" strokeOpacity="0.4" strokeWidth="1" vectorEffect="non-scaling-stroke" />}
          </>
        ) : (
          <path d={`M0,${height / 2} L${width},${height / 2}`} fill="none" stroke="currentColor" strokeOpacity="0.35" strokeWidth="1.5" strokeDasharray="3 4" vectorEffect="non-scaling-stroke" />
        )}
      </svg>
      {last && (
        <span
          className="absolute size-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-current shadow-[0_0_8px_1px_currentColor]"
          style={{ left: `${last[0]}%`, top: `${(last[1] / height) * 100}%` }}
        />
      )}
      {hovered && metric && (
        <div
          className="pointer-events-none absolute bottom-full z-20 mb-1.5 rounded-md border bg-popover px-2 py-1 text-[11px] whitespace-nowrap text-popover-foreground shadow-md"
          style={hovered.at[0] < width / 2 ? { left: `${hovered.at[0]}%` } : { right: `${width - hovered.at[0]}%` }}
        >
          <div className="text-muted-foreground">{formatAbsolute(hovered.point.at)}</div>
          <div>
            <span className="font-semibold tabular-nums">{formatMetric(hovered.point.value, metric.unit)}</span> {metric.name}
          </div>
          {overlay && overlay.points.length > 0 && (
            <div className="text-muted-foreground">
              <span className="font-semibold text-foreground tabular-nums">{formatMetric(valueAt(overlay.points, ms(hovered.point))!, overlay.unit)}</span> {overlay.name}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const NO_POINTS: DeskMetricPoint[] = [];

/** The overlay's name and latest value next to the chart's, with its dashed swatch. */
export function OverlayLegend({ overlay, className }: { overlay: DeskMetric; className?: string }) {
  return (
    <span className={cn('inline-flex min-w-0 items-center gap-1', className)} title={t('{name}: the dashed line over the chart, on its own scale', { name: overlay.name })}>
      <svg aria-hidden viewBox="0 0 10 2" className="h-0.5 w-2.5 shrink-0 text-foreground/45">
        <path d="M0,1 H10" stroke="currentColor" strokeWidth="2" strokeDasharray="3 2" />
      </svg>
      {overlay.latest && <span className="font-semibold text-foreground tabular-nums">{formatMetric(overlay.latest.value, overlay.unit)}</span>}
      <span className="truncate">{overlay.name}</span>
    </span>
  );
}

/**
 * The figures the Lead put on the card (record_metric card=true), e.g. "4 In progress · 2 Ready · 12 Live"; a figure
 * with a link opens it (e.g. the view listing the items behind it).
 */
export function CardFigures({ metrics, className }: { metrics: DeskMetric[]; className?: string }) {
  return (
    <div className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground', className)}>
      {metrics.map((m) => {
        const body = (
          <>
            <span className="font-semibold text-foreground tabular-nums">{m.latest ? formatMetric(m.latest.value, m.unit) : '—'}</span>
            <span className="truncate">{m.name}</span>
            <MetricTrend metric={m} className="text-[10px]" />
            {m.queryError && <span className="font-semibold text-amber-600 dark:text-amber-400">!</span>}
          </>
        );
        const title =
          `${m.name}${m.goalTitle ? ` · ${t('Goal: {goal}', { goal: m.goalTitle })}` : ''}` +
          (m.query ? ` · ${t('Live from the workspace database')}` : m.latest ? ` · ${formatAbsolute(m.latest.at)}` : '') +
          (m.queryError ? ` · ${t('Its query fails, showing the last good value: {error}', { error: m.queryError })}` : '');
        return m.link ? (
          <Link key={m.id} to={m.link} title={title} className="inline-flex min-w-0 max-w-full items-baseline gap-1 rounded hover:text-foreground hover:underline">
            {body}
          </Link>
        ) : (
          <span key={m.id} title={title} className="inline-flex min-w-0 max-w-full items-baseline gap-1">
            {body}
          </span>
        );
      })}
    </div>
  );
}
